import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts'
import { ClipboardList, CheckCircle2, RefreshCw, Timer, AlertTriangle, PauseCircle, Radio, WifiOff, Terminal, Activity, HardDrive, Wallet, Receipt, AlertCircle, Gift } from 'lucide-react'
import api from '../api'
import { Card, PageHeader, Input, Select } from '../components/ui'

// Kartu ringkasan "perlu perhatian hari ini" — link ke halaman terkait. `ok=true` (default) artinya
// tampilkan abu-abu netral (tidak perlu perhatian), `ok=false` artinya tampilkan warna `tone`.
function KartuPerhatian({ icon: Icon, label, value, to, tone, ok = true }) {
  const colors = !ok
    ? { color: `var(--${tone})`, bg: `var(--${tone}-soft)` }
    : { color: 'var(--text-faint)', bg: '#f0f0f3' }
  return (
    <Link to={to} style={{ textDecoration: 'none' }}>
      <Card style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}>
        <div style={{
          width: 36, height: 36, borderRadius: 9, flexShrink: 0, background: colors.bg,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <Icon size={17} color={colors.color} strokeWidth={2} />
        </div>
        <div>
          <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text)', letterSpacing: -0.3 }}>{value}</div>
          <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 1 }}>{label}</div>
        </div>
      </Card>
    </Link>
  )
}

// Log server mentah (admin-app + admin-bot) yg selama ini cuma bisa dibaca lewat `docker logs` --
// diminta user 2026-07-18 supaya bisa dipantau dari Dashboard tanpa akses SSH/terminal. Komponen
// mandiri (state + polling sendiri) biar tidak numpuk ke state utama Dashboard, sama pola dengan
// halaman lain di app ini yg punya bagian mandiri (mis. DiagnostikModal).
function PanelLogSistem() {
  const [logs, setLogs] = useState([])
  const [sumber, setSumber] = useState('all')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    load()
    const t = setInterval(load, 5000)
    return () => clearInterval(t)
  }, [])

  async function load() {
    try {
      const res = await api.get('/system-log')
      setLogs(res.data)
    } catch (e) { /* panel log tidak boleh ganggu tampilan Dashboard kalau gagal load */ }
    finally { setLoading(false) }
  }

  const filtered = sumber === 'all' ? logs : logs.filter(l => l.sumber === sumber)
  const terbaruDulu = [...filtered].reverse()

  function fmtJam(ts) {
    return new Date(ts).toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
  }

  return (
    <Card style={{ padding: 20, marginTop: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Terminal size={16} color="var(--text-muted)" />
          <h3 style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--text)', margin: 0 }}>Log Sistem</h3>
          <span style={{ fontSize: 11, color: 'var(--text-faint)' }}>({filtered.length} baris, auto-refresh tiap 5 detik)</span>
        </div>
        <Select value={sumber} onChange={e => setSumber(e.target.value)} style={{ width: 150 }}>
          <option value="all">Semua Sumber</option>
          <option value="app">Admin App</option>
          <option value="bot">Bot WA</option>
        </Select>
      </div>
      <div style={{
        background: '#111318', borderRadius: 10, padding: '10px 14px', maxHeight: 380, overflowY: 'auto',
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12, lineHeight: 1.7
      }}>
        {loading ? (
          <div style={{ color: '#6b7280' }}>Memuat log...</div>
        ) : terbaruDulu.length === 0 ? (
          <div style={{ color: '#6b7280' }}>Belum ada log.</div>
        ) : terbaruDulu.map((l, i) => (
          <div key={i} style={{ display: 'flex', gap: 8, color: l.level === 'error' ? '#f87171' : '#d1d5db', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            <span style={{ color: '#6b7280', flexShrink: 0 }}>{fmtJam(l.ts)}</span>
            <span style={{ flexShrink: 0, color: l.sumber === 'bot' ? '#38bdf8' : '#a78bfa' }}>[{l.sumber === 'bot' ? 'BOT' : 'APP'}]</span>
            <span>{l.text}</span>
          </div>
        ))}
      </div>
    </Card>
  )
}

function rp(n) { return 'Rp ' + Number(n || 0).toLocaleString('id-ID') }

const COLORS = ['#4f46e5', '#4cc9f0', '#f72585', '#7c3aed', '#0ea5e9', '#f59e0b', '#10b981']

function KartuStat({ icon: Icon, label, value, color, bg }) {
  return (
    <Card style={{ padding: '18px 20px', display: 'flex', alignItems: 'center', gap: 16 }}>
      <div style={{
        width: 44, height: 44, borderRadius: 11, flexShrink: 0, background: bg,
        display: 'flex', alignItems: 'center', justifyContent: 'center'
      }}>
        <Icon size={20} color={color} strokeWidth={2} />
      </div>
      <div>
        <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--text)', letterSpacing: -0.5 }}>{value}</div>
        <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 2 }}>{label}</div>
      </div>
    </Card>
  )
}

function bulanIni() {
  const now = new Date()
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
}

export default function Dashboard() {
  const [stats, setStats] = useState(null)
  const [billingStats, setBillingStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const [perhatian, setPerhatian] = useState(null)
  const [periode, setPeriode] = useState(bulanIni)

  useEffect(() => { loadStats() }, [periode])
  useEffect(() => { loadPerhatian(); const t = setInterval(loadPerhatian, 30000); return () => clearInterval(t) }, [])

  // Ringkasan "perlu perhatian hari ini" — sengaja gabung beberapa endpoint yang sudah ada (bukan
  // bikin endpoint baru): tiket OPEN dari halaman Tiket Gangguan, nomor AI ter-pause dari Log Chat
  // AI, status bot WA dari Bot Settings, device offline dari Perangkat Pelanggan, tagihan lewat
  // jatuh tempo dari /tagihan/ringkasan (2026-07-22, lintas periode -- TIDAK terikat filter bulan
  // yg lagi dipilih admin di header, selalu relevan ke "hari ini"), + kesehatan aplikasi sendiri
  // dari /api/health (Fase 5 rencana perbaikan, 2026-07-18). Tiap panel gagal load independen
  // (Promise.allSettled) — satu sumber down tidak menggagalkan ringkasan yang lain.
  async function loadPerhatian() {
    const [tiketRes, chatRes, botRes, devRes, healthRes, tagihanRes] = await Promise.allSettled([
      api.get('/tiket', { params: { status: 'OPEN' } }),
      api.get('/ai-chat-log/percakapan'),
      api.get('/sync/status'),
      api.get('/genieacs/devices', { params: { limit: 1 } }),
      api.get('/health'),
      api.get('/tagihan/ringkasan', { params: { periode: bulanIni() } })
    ])
    setPerhatian({
      tiketOpen: tiketRes.status === 'fulfilled' ? tiketRes.value.data.length : null,
      aiPaused: chatRes.status === 'fulfilled' ? chatRes.value.data.filter(c => c.pausedUntil).length : null,
      botConnected: botRes.status === 'fulfilled' ? !!botRes.value.data.connected : null,
      deviceOffline: devRes.status === 'fulfilled' ? (devRes.value.data.stats.total - devRes.value.data.stats.online) : null,
      cronStale: healthRes.status === 'fulfilled' ? healthRes.value.data.cronStale : null,
      lastScanMinutesAgo: healthRes.status === 'fulfilled' ? healthRes.value.data.lastScanMinutesAgo : null,
      fotoSizeMb: healthRes.status === 'fulfilled' ? healthRes.value.data.fotoSizeMb : null,
      fotoSizeWarning: healthRes.status === 'fulfilled' ? healthRes.value.data.fotoSizeWarning : null,
      tagihanOverdue: tagihanRes.status === 'fulfilled' ? tagihanRes.value.data.jumlah_overdue_lintas_periode : null
    })
  }

  async function loadStats() {
    setLoading(true)
    try {
      const [y, m] = periode.split('-')
      const dari = `${y}-${m}-01`
      const sampai = `${y}-${m}-${new Date(y, m, 0).getDate()}`
      const [res, billingRes] = await Promise.allSettled([
        api.get(`/pekerjaan/statistik?dari=${dari}&sampai=${sampai}`),
        api.get('/tagihan/ringkasan', { params: { periode } })
      ])
      if (res.status === 'fulfilled') setStats(res.value.data)
      setBillingStats(billingRes.status === 'fulfilled' ? billingRes.value.data : null)
    } catch (e) { console.error(e) }
    finally { setLoading(false) }
  }

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300, color: 'var(--text-faint)', fontSize: 13.5 }}>
      Memuat data...
    </div>
  )

  return (
    <div>
      <PageHeader
        title="Dashboard"
        subtitle="Statistik laporan teknisi"
        actions={<Input type="month" value={periode} onChange={e => setPeriode(e.target.value)} style={{ width: 160 }} />}
      />

      {/* Perlu perhatian hari ini */}
      {perhatian && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8 }}>
            Perlu Perhatian Hari Ini
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
            <KartuPerhatian icon={AlertTriangle} label="Tiket Terbuka" value={perhatian.tiketOpen ?? '-'} to="/tiket-gangguan" tone="warning" ok={!perhatian.tiketOpen} />
            <KartuPerhatian icon={PauseCircle} label="AI Sedang Dijeda" value={perhatian.aiPaused ?? '-'} to="/log-chat-ai" tone="warning" ok={!perhatian.aiPaused} />
            <KartuPerhatian icon={Radio} label="Status Bot WA" value={perhatian.botConnected === false ? 'Terputus' : perhatian.botConnected === true ? 'Tersambung' : '-'} to="/bot" tone="danger" ok={perhatian.botConnected !== false} />
            <KartuPerhatian icon={WifiOff} label="Device Offline" value={perhatian.deviceOffline ?? '-'} to="/perangkat" tone="danger" ok={!perhatian.deviceOffline} />
            <KartuPerhatian icon={Activity} label="Scan Redaman" value={perhatian.lastScanMinutesAgo != null ? `${perhatian.lastScanMinutesAgo} mnt lalu` : '-'} to="/tiket-gangguan" tone="danger" ok={!perhatian.cronStale} />
            <KartuPerhatian icon={HardDrive} label="Ukuran Foto" value={perhatian.fotoSizeMb != null ? `${perhatian.fotoSizeMb} MB` : '-'} to="/foto" tone="warning" ok={!perhatian.fotoSizeWarning} />
            <KartuPerhatian icon={AlertCircle} label="Tagihan Lewat Jatuh Tempo" value={perhatian.tagihanOverdue ?? '-'} to="/tagihan" tone="danger" ok={!perhatian.tagihanOverdue} />
          </div>
        </div>
      )}

      {/* Kartu statistik */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 16, marginBottom: 24 }}>
        <KartuStat icon={ClipboardList} label="Total Pekerjaan" value={stats?.total || 0} color="var(--brand)" bg="var(--brand-soft)" />
        <KartuStat icon={CheckCircle2} label="Selesai" value={stats?.selesai || 0} color="var(--success)" bg="var(--success-soft)" />
        <KartuStat icon={RefreshCw} label="Sedang Proses" value={stats?.proses || 0} color="var(--warning)" bg="var(--warning-soft)" />
        <KartuStat icon={Timer} label="Rata-rata Durasi" value={`${stats?.avg_durasi || 0} mnt`} color="#0ea5e9" bg="#e6f6fd" />
      </div>

      {/* Ringkasan Tagihan Bulan Ini (2026-07-22) -- ikut filter bulan yg sama dgn statistik pekerjaan
          di header, beda dari kartu "Tagihan Lewat Jatuh Tempo" di atas yg lintas periode/hari ini. */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 16, marginBottom: 24 }}>
        <KartuStat icon={Receipt} label="Total Ditagih Bulan Ini" value={rp(billingStats?.total_tagihan)} color="var(--brand)" bg="var(--brand-soft)" />
        <KartuStat icon={CheckCircle2} label="Sudah Dibayar" value={rp(billingStats?.total_dibayar)} color="var(--success)" bg="var(--success-soft)" />
        <KartuStat icon={AlertTriangle} label="Belum Lunas" value={rp(billingStats?.sisa_belum_lunas)} color="var(--danger)" bg="var(--danger-soft)" />
        <KartuStat icon={Wallet} label="Pelanggan Belum Lunas" value={(billingStats?.jumlah_sebagian || 0) + (billingStats?.jumlah_belum_lunas || 0)} color="var(--warning)" bg="var(--warning-soft)" />
        {billingStats?.total_diskon > 0 && (
          <KartuStat icon={Gift} label="Diskon Bonus Bulan Ini" value={rp(billingStats.total_diskon)} color="var(--brand)" bg="var(--brand-soft)" />
        )}
      </div>

      <div className="grid-2" style={{ gap: 20, marginBottom: 24 }}>
        {/* Bar chart per teknisi */}
        <Card style={{ padding: 20 }}>
          <h3 style={{ fontSize: 13.5, fontWeight: 700, marginBottom: 16, color: 'var(--text)' }}>Pekerjaan per Teknisi</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={stats?.per_teknisi || []}>
              <XAxis dataKey="nama" tick={{ fontSize: 11, fill: 'var(--text-muted)' }} axisLine={{ stroke: 'var(--border)' }} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: 'var(--text-muted)' }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ borderRadius: 10, border: '1px solid var(--border)', fontSize: 12 }} />
              <Bar dataKey="total" fill="var(--brand)" radius={[4, 4, 0, 0]} name="Total" />
              <Bar dataKey="selesai" fill="var(--success)" radius={[4, 4, 0, 0]} name="Selesai" />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* Pie chart per jenis */}
        <Card style={{ padding: 20 }}>
          <h3 style={{ fontSize: 13.5, fontWeight: 700, marginBottom: 16, color: 'var(--text)' }}>Jenis Pekerjaan</h3>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={stats?.per_jenis || []} dataKey="total" nameKey="nama_jenis"
                cx="50%" cy="50%" outerRadius={80} label={({ kode, total }) => `${kode}: ${total}`}>
                {(stats?.per_jenis || []).map((_, i) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip contentStyle={{ borderRadius: 10, border: '1px solid var(--border)', fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Tabel performa teknisi */}
      <Card style={{ padding: 20 }}>
        <h3 style={{ fontSize: 13.5, fontWeight: 700, marginBottom: 16, color: 'var(--text)' }}>Performa Teknisi</h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr>
                {['Teknisi', 'Total', 'Selesai', 'Proses', 'Rata-rata Durasi', 'Completion Rate'].map(h => (
                  <th key={h} style={{ padding: '9px 14px', textAlign: 'left', fontWeight: 600, fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.3, borderBottom: '1px solid var(--border)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(stats?.per_teknisi || []).map((t, i) => {
                const rate = t.total > 0 ? Math.round((t.selesai / t.total) * 100) : 0
                return (
                  <tr key={i} style={{ borderTop: '1px solid var(--border)' }}>
                    <td style={{ padding: '11px 14px', fontWeight: 500 }}>{t.nama}</td>
                    <td style={{ padding: '11px 14px' }}>{t.total}</td>
                    <td style={{ padding: '11px 14px', color: 'var(--success)', fontWeight: 600 }}>{t.selesai}</td>
                    <td style={{ padding: '11px 14px', color: 'var(--warning)' }}>{t.total - t.selesai}</td>
                    <td style={{ padding: '11px 14px' }}>{t.avg_durasi ? `${t.avg_durasi} mnt` : '-'}</td>
                    <td style={{ padding: '11px 14px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ flex: 1, background: '#f0f0f3', borderRadius: 99, height: 6 }}>
                          <div style={{ width: `${rate}%`, background: 'var(--brand)', borderRadius: 99, height: 6 }} />
                        </div>
                        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--brand)' }}>{rate}%</span>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <PanelLogSistem />
    </div>
  )
}
